import { useState, useEffect, useCallback } from 'react';
import { ModelParameters, UseModelConfigurationReturn } from '../types';
import { getDefaultParams, NEURAL_NETWORK_MODELS, SVR_MODELS, LGBMR_MODELS } from '../constants';

/**
 * Custom hook for ModelConfiguration state management and handlers
 */
export const useModelConfiguration = (
  value: ModelParameters,
  onChange: (params: ModelParameters) => void
): UseModelConfigurationReturn => {
  const [modelParams, setModelParams] = useState<ModelParameters>(value);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Sync with parent value when it changes externally
  useEffect(() => {
    setModelParams(value);
  }, [value]);

  const handleParameterChange = useCallback((
    paramName: keyof ModelParameters,
    paramValue: string | number | boolean | undefined
  ) => {
    const updatedParams = {
      ...modelParams,
      [paramName]: paramValue
    };
    setModelParams(updatedParams);
    onChange(updatedParams);
  }, [modelParams, onChange]);

  const handleModelTypeChange = useCallback((newModelType: string) => {
    const newParams = getDefaultParams(newModelType);
    setModelParams(newParams);
    onChange(newParams);
  }, [onChange]);

  // Check if current model is a neural network model
  const isNeuralNetworkModel = useCallback(() => {
    return NEURAL_NETWORK_MODELS.includes(modelParams.MODE as typeof NEURAL_NETWORK_MODELS[number]);
  }, [modelParams.MODE]);

  // Check if current model is SVR model
  const isSVRModel = useCallback(() => {
    return SVR_MODELS.includes(modelParams.MODE as typeof SVR_MODELS[number]);
  }, [modelParams.MODE]);

  // Check if current model is Linear model
  const isLinearModel = useCallback(() => {
    return modelParams.MODE === 'LIN';
  }, [modelParams.MODE]);

  // Check if current model is LGBMR model
  const isLGBMRModel = useCallback(() => {
    return LGBMR_MODELS.includes(modelParams.MODE as typeof LGBMR_MODELS[number]);
  }, [modelParams.MODE]);

  return {
    modelParams,
    validationErrors,
    handleParameterChange,
    handleModelTypeChange,
    isNeuralNetworkModel,
    isSVRModel,
    isLinearModel,
    isLGBMRModel,
    setValidationErrors
  };
};
